const HR_RESULTS_SELECTOR = '.hr-results';
const HR_SEARCH_SUGGESTIONS_SELECTOR = '.search__content';
const HR_SEARCH_ACTIVE_CLASS = 'hr-search-disable-scroll';
const HR_SEARCH_FORM_SELECTOR =
  `${HR_RESULTS_SELECTOR} form[data-type="add-to-cart-form"], ${HR_SEARCH_SUGGESTIONS_SELECTOR} form[data-type="add-to-cart-form"]`;
const PRODUCT_HANDLE_RE = /\/products\/([^/?#]+)/;
const DEBUG_SEARCH_NOTIFY = false;

let scanScheduled = false;
const productJsonCache = new Map();

function debugSearchNotify(step, data = {}) {
  if (!DEBUG_SEARCH_NOTIFY) return;

  console.log(`[Search Notify] ${step}`, data);
}

function getShopifyRoot() {
  const root = window.Shopify?.routes?.root || '/';

  return root.endsWith('/') ? root : `${root}/`;
}

function getLocalizedProductUrl(handle, includeJson = false) {
  if (!handle) return '';

  const suffix = includeJson ? '.js' : '';

  return `${getShopifyRoot()}products/${encodeURIComponent(handle)}${suffix}`;
}

function parseProductHandle(url) {
  if (!url) return '';

  try {
    const path = url.startsWith('http')
      ? new URL(url).pathname
      : url.split('?')[0].split('#')[0];

    return path.match(PRODUCT_HANDLE_RE)?.[1] || '';
  } catch {
    return url.match(PRODUCT_HANDLE_RE)?.[1] || '';
  }
}

function parseShopifyId(value) {
  if (value == null || value === '') return '';

  const stringValue = String(value).trim();
  const gidMatch = stringValue.match(/\/(\d+)$/);

  return gidMatch?.[1] || stringValue;
}

function getNotifyLabel() {
  return (
    window.FoxTheme?.variantStrings?.notifyMeWhenAvailable ||
    'Notify me when available'
  );
}

function getAddToCartLabel() {
  return (
    window.FoxTheme?.variantStrings?.addToCart ||
    window.FoxTheme?.strings?.addToCart ||
    'Add to cart'
  );
}

function getChooseOptionsLabel() {
  return (
    window.FoxTheme?.variantStrings?.chooseOptions ||
    'Choose options'
  );
}

function getChooseOptionsShortLabel() {
  return (
    window.FoxTheme?.variantStrings?.chooseOptionsShort ||
    getChooseOptionsLabel()
  );
}

function isKbiteNotifyEnabled() {
  return window.FoxTheme?.settings?.pcardEnableKbiteNotify !== false;
}

function productRequiresChooseOptions(product, variant) {
  if (!product?.variants || product.variants.length > 1) {
    return true;
  }

  const rule = variant?.quantity_rule;

  if (!rule) {
    return false;
  }

  return (
    rule.min > 1 ||
    rule.max != null ||
    rule.increment > 1
  );
}

function useShortChooseOptionsLabel(form) {
  return Boolean(form.closest(HR_SEARCH_SUGGESTIONS_SELECTOR));
}

function getButtonStyleClass() {
  return (
    document.querySelector('product-card-notify')?.dataset.buttonStyle ||
    'btn--primary'
  );
}

function getCartIconMarkup(form) {
  return (
    form.querySelector('.product-card__atc-icon')?.innerHTML?.trim() ||
    document
      .querySelector('.product-card .product-card__atc-icon')
      ?.innerHTML?.trim() ||
    ''
  );
}

function getProductCard(form) {
  return (
    form.closest('.product-card') ||
    form.closest('.hr-search-overlay-product') ||
    form.closest('.swiper-slide') ||
    form.parentElement
  );
}

function getProductLink(form) {
  const card = getProductCard(form);

  return (
    card?.querySelector('a[href*="/products/"]') ||
    form
      .closest('.product-card__image-wrapper')
      ?.querySelector('a[href*="/products/"]') ||
    null
  );
}

function readProductIdFromCard(card) {
  if (!card) return '';

  const source =
    card.querySelector('[data-product-id]') ||
    (card.matches?.('[data-product-id]') ? card : null);

  return parseShopifyId(
    source?.dataset?.productId ||
      source?.getAttribute('data-product-id')
  );
}

function readProductIdFromForm(form) {
  const inputValue = form.querySelector(
    'input[name="product-id"]'
  )?.value;

  if (inputValue) {
    return parseShopifyId(inputValue);
  }

  const formValue =
    form.dataset.productId ||
    form.getAttribute('data-product-id');

  if (formValue) {
    return parseShopifyId(formValue);
  }

  return readProductIdFromCard(getProductCard(form));
}

function readVariantIdFromForm(form) {
  const sources = [
    form.querySelector('input[name="id"]'),
    form.querySelector('input[name="variant-id"]'),
    form.querySelector('.product-variant-id'),
    form.querySelector('[data-variant-id]'),
  ];

  for (const source of sources) {
    const value =
      source?.value ||
      source?.dataset?.variantId ||
      source?.getAttribute('data-variant-id');

    if (value) {
      return parseShopifyId(value);
    }
  }

  return '';
}

async function fetchProductByHandle(handle) {
  if (!handle) return null;

  const url = getLocalizedProductUrl(handle, true);

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      debugSearchNotify('product-fetch-failed', {
        handle,
        url,
        status: response.status,
      });

      return null;
    }

    const text = await response.text();

    if (!text) {
      debugSearchNotify('product-fetch-empty', {
        handle,
        url,
      });

      return null;
    }

    return JSON.parse(text);
  } catch (error) {
    debugSearchNotify('product-fetch-error', {
      handle,
      url,
      error,
    });

    return null;
  }
}

async function fetchCachedProductByHandle(handle) {
  if (!handle) return null;

  const cacheKey = `${getShopifyRoot()}:${handle}`;

  if (productJsonCache.has(cacheKey)) {
    return productJsonCache.get(cacheKey);
  }

  const productPromise = fetchProductByHandle(handle).catch(
    (error) => {
      productJsonCache.delete(cacheKey);

      debugSearchNotify('product-cache-error', {
        handle,
        cacheKey,
        error,
      });

      return null;
    }
  );

  productJsonCache.set(cacheKey, productPromise);

  return productPromise;
}

async function resolveFormProductContext(form) {
  const link = getProductLink(form);
  const href = link?.getAttribute('href') || link?.href || '';
  const handle = parseProductHandle(href);

  const variantId = readVariantIdFromForm(form);
  let productId = readProductIdFromForm(form);

  if (!handle) {
    debugSearchNotify('skip:missing-handle', {
      form,
    });

    return null;
  }

  if (!variantId) {
    debugSearchNotify('skip:missing-variant-id', {
      handle,
      form,
    });

    return null;
  }

  const product = await fetchCachedProductByHandle(handle);

  if (!product) {
    debugSearchNotify('skip:product-fetch-failed', {
      handle,
      form,
    });

    return null;
  }

  if (!productId) {
    productId = parseShopifyId(product.id);
  }

  const variant = product.variants?.find(
    (item) => parseShopifyId(item.id) === variantId
  );

  if (!variant) {
    debugSearchNotify('skip:variant-not-found', {
      handle,
      productId,
      variantId,
      form,
    });

    return null;
  }

  return {
    handle,
    productId,
    variantId,
    product,
    variant,
  };
}

function createChooseOptionsLink(form, context, useShortLabel = false) {
  const { handle } = context;
  const label = useShortLabel
    ? getChooseOptionsShortLabel()
    : getChooseOptionsLabel();
  const link = document.createElement('a');

  link.href = getLocalizedProductUrl(handle);
  link.className = [
    'product-card__atc',
    'product-card__action-button',
    'btn',
    getButtonStyleClass(),
  ].join(' ');

  link.setAttribute('aria-label', getChooseOptionsLabel());

  link.innerHTML = `
    <div class="btn__text flex gap-1 items-center">
      <span class="product-card__atc-icon product-card__action-icon inline-flex md:hidden">
        ${getCartIconMarkup(form)}
      </span>
      <span class="product-card__atc-text product-card__action-text">
        ${label}
      </span>
    </div>
  `;

  return link;
}

function replaceWithChooseOptionsLink(form, context) {
  if (form.dataset.searchChooseOptionsReplaced === 'true') {
    return;
  }

  const actions = document.createElement('div');
  actions.className = form.className;
  actions.dataset.searchChooseOptionsReplaced = 'true';
  actions.appendChild(
    createChooseOptionsLink(
      form,
      context,
      useShortChooseOptionsLabel(form)
    )
  );

  form.replaceWith(actions);

  debugSearchNotify('replaced-choose-options-link', {
    handle: context.handle,
    productId: context.productId,
    form,
  });
}

function createNotifyButton(form, context) {
  const { handle, productId, variantId } = context;
  const notifyLabel = getNotifyLabel();
  const button = document.createElement('button');

  console.log(button)

  button.type = 'button';

  button.className = [
    'product-card__atc',
    'product-card__action-button',
    'btn',
    'btn--secondary',
    'product-form__notify-trigger',
    'product-card__notify',
  ].join(' ');

  button.dataset.action = 'open-product-card-notify';
  button.dataset.productId = String(productId);
  button.dataset.productHandle = handle;
  button.dataset.variantId = String(variantId);
  button.dataset.productUrl = getLocalizedProductUrl(handle);

  button.setAttribute('aria-label', notifyLabel);

  button.innerHTML = `
    <div class="btn__text flex gap-1 items-center">
      <span class="product-card__atc-icon product-card__action-icon inline-flex md:hidden">
        ${getCartIconMarkup(form)}
      </span>
      <span class="product-card__atc-text product-card__action-text">
        ${notifyLabel}
      </span>
    </div>
  `;

  return button;
}

function restoreAvailableButton(form) {
  const button = form.querySelector(
    'button[type="submit"], button[name="add"], button'
  );

  if (!button) return;

  const addToCartLabel = getAddToCartLabel();

  button.type = 'submit';
  button.name = 'add';
  button.disabled = false;

  button.removeAttribute('disabled');
  button.removeAttribute('aria-disabled');

  button.classList.remove(
    'product-form__notify-trigger',
    'product-card__notify'
  );

  delete button.dataset.action;
  delete button.dataset.productId;
  delete button.dataset.productHandle;
  delete button.dataset.variantId;
  delete button.dataset.productUrl;

  button.removeAttribute('data-action');
  button.removeAttribute('data-product-id');
  button.removeAttribute('data-product-handle');
  button.removeAttribute('data-variant-id');
  button.removeAttribute('data-product-url');

  button.setAttribute('aria-label', addToCartLabel);

  const textElement =
    button.querySelector('.product-card__atc-text') ||
    button.querySelector('.product-card__action-text') ||
    button.querySelector('.btn__text');

  if (textElement) {
    textElement.textContent = addToCartLabel;
  }

  form.removeAttribute('data-search-notify-replaced');
  delete form.dataset.searchNotifyReplaced;

  debugSearchNotify('restored-available-button', {
    form,
    label: addToCartLabel,
  });
}

function shouldInspectForm(form) {
  if (form.dataset.searchNotifyPending === 'true') return false;
  if (form.dataset.searchChooseOptionsReplaced === 'true') return false;

  return Boolean(
    form.querySelector(
      'button[type="submit"], button[name="add"], button'
    )
  );
}

async function inspectAndUpdateForm(form) {
  if (!shouldInspectForm(form)) return;

  form.dataset.searchNotifyPending = 'true';

  try {
    const context = await resolveFormProductContext(form);

    if (!context) return;

    debugSearchNotify('variant-resolved', {
      handle: context.handle,
      productId: context.productId,
      variantId: context.variantId,
      available: context.variant.available,
      productJsonUrl: getLocalizedProductUrl(
        context.handle,
        true
      ),
      form,
    });

    const needsChooseOptions = productRequiresChooseOptions(
      context.product,
      context.variant
    );

    if (needsChooseOptions) {
      if (
        context.variant.available === false &&
        isKbiteNotifyEnabled()
      ) {
        if (form.dataset.searchNotifyReplaced === 'true') {
          return;
        }

        if (
          form.querySelector(
            '[data-action="open-product-card-notify"]'
          )
        ) {
          return;
        }

        const oldButton = form.querySelector(
          'button[type="submit"], button[name="add"], button'
        );

        if (!oldButton) {
          debugSearchNotify('skip:button-missing', {
            form,
          });

          return;
        }

        const currentVariantId = readVariantIdFromForm(form);

        if (currentVariantId !== context.variantId) {
          debugSearchNotify('skip:variant-changed', {
            previousVariantId: context.variantId,
            currentVariantId,
            form,
          });

          return;
        }

        const notifyButton = createNotifyButton(form, context);

        oldButton.replaceWith(notifyButton);

        form.dataset.searchNotifyReplaced = 'true';

        debugSearchNotify('replaced-button', {
          handle: context.handle,
          productId: context.productId,
          variantId: context.variantId,
          productUrl: notifyButton.dataset.productUrl,
          form,
        });

        return;
      }

      replaceWithChooseOptionsLink(form, context);
      return;
    }

    /*
     * Hello Retail may incorrectly render an available Shopify variant
     * as disabled and "Sold out". Repair that button here.
     */
    if (context.variant.available === true) {
      restoreAvailableButton(form);
      return;
    }

    if (context.variant.available !== false || !isKbiteNotifyEnabled()) {
      return;
    }

    if (form.dataset.searchNotifyReplaced === 'true') {
      return;
    }

    if (
      form.querySelector(
        '[data-action="open-product-card-notify"]'
      )
    ) {
      return;
    }

    const oldButton = form.querySelector(
      'button[type="submit"], button[name="add"], button'
    );

    if (!oldButton) {
      debugSearchNotify('skip:button-missing', {
        form,
      });

      return;
    }

    const currentVariantId = readVariantIdFromForm(form);

    if (currentVariantId !== context.variantId) {
      debugSearchNotify('skip:variant-changed', {
        previousVariantId: context.variantId,
        currentVariantId,
        form,
      });

      return;
    }

    const notifyButton = createNotifyButton(form, context);

    oldButton.replaceWith(notifyButton);

    form.dataset.searchNotifyReplaced = 'true';

    debugSearchNotify('replaced-button', {
      handle: context.handle,
      productId: context.productId,
      variantId: context.variantId,
      productUrl: notifyButton.dataset.productUrl,
      form,
    });
  } finally {
    delete form.dataset.searchNotifyPending;
  }
}

async function scanSearchCards() {
  if (!hasActiveHelloRetailSearch()) return;

  const forms = [
    ...document.querySelectorAll(HR_SEARCH_FORM_SELECTOR),
  ].filter(shouldInspectForm);

  debugSearchNotify('forms-found', {
    count: forms.length,
    locale: window.Shopify?.locale,
    country: window.Shopify?.country,
    root: getShopifyRoot(),
    forms,
  });

  await Promise.all(forms.map(inspectAndUpdateForm));
}

function hasActiveHelloRetailSearch() {
  return Boolean(
    document.documentElement.classList.contains(
      HR_SEARCH_ACTIVE_CLASS
    ) ||
      document.body.classList.contains(
        HR_SEARCH_ACTIVE_CLASS
      ) ||
      document.querySelector(HR_SEARCH_FORM_SELECTOR)
  );
}

function scheduleScan() {
  if (scanScheduled) return;

  scanScheduled = true;

  requestAnimationFrame(() => {
    scanScheduled = false;
    void scanSearchCards();
  });
}

function mutationsTouchHelloRetailProducts(mutations) {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;

      if (
        node.matches?.(
          `${HR_RESULTS_SELECTOR},
          [class*="hr-"],
          [id*="helloretail"],
          form[data-type="add-to-cart-form"]`
        ) ||
        node.querySelector?.(
          `${HR_RESULTS_SELECTOR},
          [class*="hr-"],
          [id*="helloretail"],
          form[data-type="add-to-cart-form"]`
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

function initSearchProductCardNotify() {
  const observer = new MutationObserver((mutations) => {
    if (mutationsTouchHelloRetailProducts(mutations)) {
      scheduleScan();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  scheduleScan();
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initSearchProductCardNotify,
    { once: true }
  );
} else {
  initSearchProductCardNotify();
}